import React, {FC, useState, useEffect} from "react";
import axios from "axios";

const CommentAuthorCard: FC<{
  fullName: string;
  date: string;
  avatar?: string;
}> = ({fullName, date, avatar}) => {
  const defaultAvatar = "/mocks/no-name.png";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "8px",
      }}
    >
      <img
        src={avatar || defaultAvatar}
        width={28}
        height={28}
        alt="Avatar"
        style={{borderRadius: "50%"}}
      />
      <div style={{display: "flex", flexDirection: "column"}}>
        <span style={{color: "#FAFAFA", fontSize: "12px", fontWeight: "500"}}>
          {fullName}
        </span>
        <span style={{color: "rgba(255, 255, 255, 0.5)", fontSize: "10px"}}>
          {date}
        </span>
      </div>
    </div>
  );
};

interface ILocalComment {
  id: string;
  worker: string;
  text: string;
  date: string;
  avatar?: string;
}

interface IBlockCommentsProps {
  initialComments?: ILocalComment[];
  placeholder?: string;
  apiUrl?: string;
  apiKey?: string;
  initiativeId?: string;
  personId?: string; // Добавляем поддержку ID работника
  authorId?: string;
  currentUserName?: string;
}

export const BlockComments: FC<IBlockCommentsProps> = ({
  initialComments = [],
  placeholder = "Введите ваш комментарий...",
  apiUrl,
  apiKey,
  initiativeId,
  personId, // Добавляем personId
  authorId,
  currentUserName,
}) => {
  const [comments, setComments] = useState<ILocalComment[]>(initialComments);
  const [commentText, setCommentText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Синхронизируем состояние с props при их изменении
  useEffect(() => {
    setComments(initialComments);
  }, [initialComments]);

  const handleSubmit = async () => {
    if (!commentText.trim()) return;

    // Сначала добавляем комментарий локально
    const newComment: ILocalComment = {
      id: Date.now().toString(),
      worker: currentUserName || "Неизвестный пользователь",
      text: commentText.trim(),
      date: new Date().toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
      avatar: "/mocks/no-name.png",
    };

    setComments((prev) => [...prev, newComment]);
    setCommentText("");

    if (apiUrl && apiKey && (initiativeId || personId) && authorId) {
      setIsSubmitting(true);
      try {
        const requestData = {
          data: {
            content: commentText.trim(),
            author: authorId,
            ...(initiativeId && {initiative: initiativeId}),
            ...(personId && {person: personId}),
          },
        };

        await axios.post(`${apiUrl}/api/comments`, requestData, {
          headers: {
            Authorization: `Bearer ${apiKey}`,
            "Content-Type": "application/json",
          },
        });
      } catch (error) {
        console.error("Ошибка при отправке комментария:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  return (
    <div
      style={{
        color: "white",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <div
        style={{
          fontSize: "16px",
          fontWeight: "500",
          color: "rgba(255, 255, 255, 0.5",
        }}
      >
        Комментарии
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {comments.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              maxHeight: "300px",
              overflowY: "auto",
              paddingRight: "4px",
            }}
          >
            {comments.map((comment) => (
              <div
                key={comment.id}
                style={{
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  padding: "12px",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                }}
              >
                <CommentAuthorCard
                  fullName={comment.worker}
                  date={comment.date}
                  avatar={comment.avatar}
                />

                <div
                  style={{
                    color: "#FAFAFA",
                    fontSize: "14px",
                    lineHeight: "1.4",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {comment.text}
                </div>
              </div>
            ))}
          </div>
        )}

        {comments.length === 0 && (
          <div
            style={{
              color: "rgba(255, 255, 255, 0.4)",
              fontSize: "14px",
              textAlign: "center",
              padding: "16px",
              fontStyle: "italic",
            }}
          >
            Комментариев пока нет. Будьте первым!
          </div>
        )}

        {comments.length > 0 && (
          <div
            style={{
              height: "1px",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            }}
          />
        )}

        <div style={{display: "flex", flexDirection: "column", gap: "12px"}}>
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={{
              width: "100%",
              minHeight: "80px",
              padding: "12px",
              border: "1px solid rgba(246, 251, 253, 0.28)",
              borderRadius: "12px",
              backgroundColor: "#22272B",
              color: "#FAFAFA",
              fontSize: "14px",
              fontFamily: "inherit",
              resize: "vertical",
              outline: "none",
              transition: "border-color 0.2s ease",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "rgba(86, 179, 255, 0.5)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "rgba(255, 255, 255, 0.2)";
            }}
          />

          <div style={{display: "flex", justifyContent: "flex-start"}}>
            <button
              onClick={handleSubmit}
              disabled={!commentText.trim() || isSubmitting}
              style={{
                padding: "8px 16px",
                border: "none",
                borderRadius: "16px",
                background:
                  commentText.trim() && !isSubmitting
                    ? "linear-gradient(180deg, #6BC1FF 0%, #3A8FE8 100%)"
                    : "linear-gradient(180deg, #2D3135 0%, #1A1E22 100%)",
                color: "#FFFFFF",
                fontSize: "14px",
                fontWeight: "500",
                cursor:
                  commentText.trim() && !isSubmitting
                    ? "pointer"
                    : "not-allowed",
                transition: "all 0.2s ease",
                opacity: commentText.trim() && !isSubmitting ? 1 : 0.6,
              }}
              onMouseEnter={(e) => {
                if (commentText.trim() && !isSubmitting) {
                  e.currentTarget.style.background =
                    "linear-gradient(180deg, #5AAFFF 0%, #2E7BD6 100%)";
                }
              }}
              onMouseLeave={(e) => {
                if (commentText.trim() && !isSubmitting) {
                  e.currentTarget.style.background =
                    "linear-gradient(180deg, #6BC1FF 0%, #3A8FE8 100%)";
                }
              }}
            >
              {isSubmitting ? "Отправка..." : "Отправить"}
            </button>
          </div>
          <br />
        </div>
      </div>
    </div>
  );
};
